import { styled } from '@mui/material';
import NoAssignments from 'components/Assignments/NoAssignments';
import { useAuth } from 'contexts/auth';
import { useJoyride } from 'contexts/joyride';
import { useTranslation } from 'react-i18next';
import { Button, Container, Icon, InstructorAssignmentCard, Stack, Text, TextInput } from 'shell/components';
import { EXAMPLE_ASSIGNMENT_ID } from 'utils/constants';

const Desktop = ({
    loading,
    isDuplicating,
    filters,
    setFilters,
    getFilterCount,
    getAPIAssignments,
    onClickCreate,
    onClick,
    onClickEdit,
    onClickDuplicate,
    onClickUnpublish,
    onClickPublish,
    onClickDelete,
    assignments,
}) => {
    const { auth } = useAuth();
    const { joyrideRun } = useJoyride();
    const { t } = useTranslation('assignment');

    return (
        <Container device={'desktop'}>
            <CustomStack spacing={5}>
                <Stack alignItems={'center'} direction={'row'} justifyContent={'space-between'}>
                    <Text component={'h1'} variant={'h6'} weight={'semibold'}>
                        {t('assignments', 'Assignments')}
                    </Text>
                    <Stack alignItems={'center'} direction={'row'} spacing={4}>
                        <CustomButton
                            color={'neutral'}
                            startIcon={<Icon kind={'filter'} />}
                            variant={'secondary'}
                            onClick={() => setFilters((current) => ({ ...current, open: true }))}
                        >
                            {t('common:controls.sort-filter', 'Sort & Filter')} ({getFilterCount(filters)})
                        </CustomButton>
                        <TextInput
                            aria-label={'search'}
                            endIcon={<Icon kind={'search'} />}
                            placeholder={t('common:controls.search', 'Search')}
                            value={filters.search}
                            onChange={(e) => setFilters((current) => ({ ...current, search: e.target.value }))}
                        />
                        <Button
                            disabled={auth.role === 'ta'}
                            loading={loading}
                            loadingGrow={loading}
                            startIcon={<Icon kind={'download'} />}
                            tooltip={
                                auth.role === 'ta'
                                    ? {
                                          component: t(
                                              'submission.instructor-first-import-feedback',
                                              'An instructor needs to import assignments before you can get started with grading and feedback.'
                                          ),
                                          maxWidth: 250,
                                      }
                                    : undefined
                            }
                            variant={'secondary'}
                            onClick={() => getAPIAssignments()}
                        >
                            {t('common:controls.import', 'Import')}
                        </Button>
                        <Button
                            className={'joyride-step-3 joyride-step-12'}
                            startIcon={<Icon kind={'add'} />}
                            onClick={onClickCreate}
                        >
                            {t('common:controls.create', 'Create')}
                        </Button>
                    </Stack>
                </Stack>
                {assignments.length ? (
                    <Stack spacing={4}>
                        {assignments
                            .filter(({ title }) => {
                                return title.toLowerCase().includes(filters.search.toLowerCase());
                            })
                            .sort((a, b) => {
                                const aTitle = a.title.toLowerCase();
                                const bTitle = b.title.toLowerCase();
                                if (filters.sort === 'title-asc') {
                                    if (aTitle < bTitle) return -1;
                                    if (aTitle > bTitle) return 1;
                                }
                                if (filters.sort === 'title-desc') {
                                    if (aTitle < bTitle) return 1;
                                    if (aTitle > bTitle) return -1;
                                }
                                return 0;
                            })
                            .map((assignment) => (
                                <InstructorAssignmentCard
                                    key={assignment.id}
                                    className={assignment.id === EXAMPLE_ASSIGNMENT_ID ? 'joyride-step-4' : undefined}
                                    assignment={assignment}
                                    disableOptions={joyrideRun}
                                    isDuplicating={isDuplicating}
                                    onClick={onClick}
                                    onClickEdit={onClickEdit}
                                    onClickDuplicate={onClickDuplicate}
                                    onClickUnpublish={onClickUnpublish}
                                    onClickPublish={onClickPublish}
                                    onClickDelete={onClickDelete}
                                />
                            ))}
                    </Stack>
                ) : (
                    <NoAssignments />
                )}
            </CustomStack>
        </Container>
    );
};

const CustomStack = styled(Stack)(({ theme }) => {
    return {
        width: '100%',
        paddingBottom: theme.spacing(5),
    };
});

const CustomButton = styled(Button)(() => {
    return {
        flexShrink: 0,
    };
});

export default Desktop;
